import { parseDate } from '../helpers/parse-date';

const invoiceHeaders = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported record. Imported row will be checked for duplicate. If a duplicate is found, the row will be skipped from import.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '';
    },
  },
  'Client Name': {
    requirementType: 'required',
    description:
      'The Name of the client associated with the invoice. This field must match the Name of a Client exactly, including spaces, punctuation and capitalization, otherwise the invoice may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  'Invoice Number': {
    requirementType: 'required',
    description:
      'Each invoice has a number, it does not need to be numeric but it must be unique otherwise the row will be skipped.',
    dataType: 'string',
    validator: () => {
      return true;
    },
  },
  'Issued On': {
    requirementType: 'required',
    description:
      'Issue Date of invoice being imported in "YYYY-MM-DD" or "MM/DD/YYYY" format. Must be before the Due Date.',
    dataType: 'date',
    validator: (value, row) => {
      const issueDate = parseDate(value);
      const dueDate = parseDate(row['Due On']);

      // Check that issue date is valid
      if (!issueDate) return false;

      // If due date exists, ensure it’s after the issue date
      if (dueDate && issueDate >= dueDate) return false;

      return true;
    },
  },
  'Due On': {
    requirementType: 'required',
    description:
      'Due Date of invoice being imported in "YYYY-MM-DD" or "MM/DD/YYYY" format. Must be after the Issue Date.',
    dataType: 'date',
    validator: (value, row) => {
      const dueDate = parseDate(value);
      const issueDate = parseDate(row['Issued On']);

      // Check that due date is valid
      if (!dueDate) return false;

      // If issue date exists, ensure due date is after issue date
      if (issueDate && dueDate <= issueDate) return false;

      return true;
    },
  },
  Status: {
    requirementType: 'required',
    description:
      'Invoice status must be either "open" or "paid" (case-insensitive). If "paid", a Client Payment record will automatically be created for the invoice during import.',
    dataType: 'string',
    validator: (value) => {
      if (!value || typeof value !== 'string') return false;

      const normalized = value.trim().toLowerCase();
      return ['open', 'paid'].includes(normalized);
    },
  },
  Currency: {
    requirementType: 'optional',
    description:
      'Provide a valid 3-letter ISO currency code (for example: USD, EUR, GBP). Leave blank to use the workspace default currency.',
    dataType: 'string',
    validator: (value) => {
      if (!value) return false;
      const cleaned = value.trim().toUpperCase();
      return /^[A-Z]{3}$/.test(cleaned);
    },
  },
  'Total Amount': {
    requirementType: 'required',
    description:
      'Total amount of the invoice after discounts and taxes are applied. Must be a postive number. Accepts commas or periods as decimal separators. All non-numeric characters will be removed before validation.',
    dataType: 'number',
    validator: (value) => {
      if (value == null || value === '') return false;

      // Normalize and parse value (remove symbols, normalize decimal)
      const cleaned = String(value)
        .replace(/[^\d.,-]/g, '') // keep digits, comma, period, minus
        .replace(',', '.');
      const num = parseFloat(cleaned);

      return !isNaN(num) && num > 0;
    },
  },

  'Discount Amount': {
    requirementType: 'optional',
    description:
      'Discount applied to the invoice total before taxes. Must be a non-negative number. Accepts commas or periods as decimal separators.',
    dataType: 'number',
    validator: (value, row) => {
      if (value == null || value === '') return true; // optional

      const cleaned = String(value)
        .replace(/[^\d.,-]/g, '')
        .replace(',', '.');
      const num = parseFloat(cleaned);

      // Discount should not exceed total
      const total = parseFloat(
        String(row['Total Amount'] || '')
          .replace(/[^\d.,-]/g, '')
          .replace(',', '.'),
      );

      return !isNaN(num) && num >= 0 && (!isNaN(total) ? num <= total : true);
    },
  },

  'Tax Amount': {
    requirementType: 'optional',
    description:
      'Tax portion of the invoice Total Amount. Must be a non-negative number and cannot exceed the total amount. Accepts commas or periods as decimal separators.',
    dataType: 'number',
    validator: (value, row) => {
      if (value == null || value === '') return true; // optional

      const cleaned = String(value)
        .replace(/[^\d.,-]/g, '')
        .replace(',', '.');
      const num = parseFloat(cleaned);

      const total = parseFloat(
        String(row['Total Amount'] || '')
          .replace(/[^\d.,-]/g, '')
          .replace(',', '.'),
      );

      return !isNaN(num) && num >= 0 && (!isNaN(total) ? num <= total : true);
    },
  },
  'Tax Percentage': {
    requirementType: 'optional',
    description:
      'Percentage rate of tax applied to the invoice (e.g., 10, 7.5, or 0). Accepts commas or periods for decimals. Must be between 0 and 100.',
    dataType: 'number',
    validator: (value) => {
      if (value == null || value === '') return true; // optional

      // Normalize and clean the input
      const cleaned = String(value)
        .replace(/[^\d.,-]/g, '')
        .replace(',', '.');
      const num = parseFloat(cleaned);

      // Must be a valid number between 0 and 100
      return !isNaN(num) && num >= 0 && num <= 100;
    },
  },
  'Tax Rate Name': {
    requirementType: 'optional',
    description:
      'Tax name applied on the invoice. It will be used to match with Custom Data > Tax Rates. If no match is found by exact name match, the named Tax Rate will not be assigned.',
    dataType: 'string',
    validator: () => true,
  },
  Subject: {
    requirementType: 'optional',
    description: 'Subject on the invoice, provides a title for the invoice details.',
    dataType: 'string',
    validator: () => true,
  },
  Notes: {
    requirementType: 'optional',
    description: 'Notes on an invoice where payment details and methods could be provided.',
    dataType: 'string',
    validator: () => true,
  },
  'Invoice Bill To': {
    requirementType: 'optional',
    description: 'Company name on the invoice that is receiving the invoice. If left blank, Client Name will be used.',
    dataType: 'string',
    validator: () => true,
  },
  'Invoice Company Name': {
    requirementType: 'optional',
    description: 'Company name on the invoice that is sending the invoice.',
    dataType: 'string',
    validator: () => true,
  },
  'Invoice Company Street Address': {
    requirementType: 'optional',
    description: 'Street address that will show on the heading of the invoice where the invoice is sent from.',
    dataType: 'string',
    validator: () => true,
  },
  'Payment Terms': {
    requirementType: 'required',
    description:
      'Payment terms assigned to the invoice. This must exactly match one of the configured terms in Custom Data > Payment Terms (e.g., "Net-30", "Net-45").',
    dataType: 'string',
    validator: () => true,
  },
  'PO Number': {
    requirementType: 'optional',
    description:
      'If provided, will be checked against existing Purchase Orders for the Client or will remain a text field if none match.',
    dataType: 'string',
    validator: () => true,
  },
};

export default invoiceHeaders;
